import {
    ADDRESS_CLAMP_TO_EDGE,
    PIXELFORMAT_RGBA16F,
    PIXELFORMAT_RGBA32F,
    SEMANTIC_POSITION,
    drawQuadWithShader,
    BoundingBox,
    GraphicsDevice,
    RenderTarget,
    ScopeSpace,
    Shader,
    ShaderUtils,
    Texture,
    Vec3,
    BlendState
} from 'playcanvas';

import { vertexShader, fragmentShader } from '../shaders/bound-shader';
import { Splat } from '../splat';

const v1 = new Vec3();
const v2 = new Vec3();
const v3 = new Vec3();
const v4 = new Vec3();

const resolve = (scope: ScopeSpace, values: any) => {
    for (const key in values) {
        scope.resolve(key).setValue(values[key]);
    }
};

function sigmoid(x: number): number {
    return 1 / (1 + Math.exp(-x));
}

/**
 * Histogram over sigmoid(opacity): return minimum alpha so roughly (1 - keepFraction) * n
 * lowest-opacity splats fall below the threshold (fast O(n), approximate percentile).
 */
function minAlphaForOpacityKeepFraction(opacity: Float32Array | null | undefined, keepFraction: number): number {
    if (!opacity || opacity.length === 0) return 0;
    const n = opacity.length;
    const clampedKeep = Math.max(0.05, Math.min(1, keepFraction));
    const dropFraction = 1 - clampedKeep;
    const dropCount = Math.floor(n * dropFraction);
    if (dropCount <= 0) return 0;

    const bins = 256;
    const hist = new Uint32Array(bins);
    for (let i = 0; i < n; i++) {
        const a = sigmoid(opacity[i]);
        let idx = Math.floor(a * bins);
        if (idx < 0) idx = 0;
        if (idx >= bins) idx = bins - 1;
        hist[idx]++;
    }

    let cum = 0;
    for (let b = 0; b < bins; b++) {
        cum += hist[b];
        if (cum > dropCount) {
            return b / bins;
        }
    }
    return 0;
}

function mergeSelectedBounds(
    width: number,
    selectedMinData: Float32Array,
    selectedMaxData: Float32Array,
    outMin: Vec3,
    outMax: Vec3
) {
    outMin.set(Infinity, Infinity, Infinity);
    outMax.set(-Infinity, -Infinity, -Infinity);

    for (let i = 0; i < width; i++) {
        const a = selectedMinData[i * 4];
        const b = selectedMinData[i * 4 + 1];
        const c = selectedMinData[i * 4 + 2];
        if (isFinite(a)) outMin.x = Math.min(outMin.x, a);
        if (isFinite(b)) outMin.y = Math.min(outMin.y, b);
        if (isFinite(c)) outMin.z = Math.min(outMin.z, c);

        const d = selectedMaxData[i * 4];
        const e = selectedMaxData[i * 4 + 1];
        const f = selectedMaxData[i * 4 + 2];
        if (isFinite(d)) outMax.x = Math.max(outMax.x, d);
        if (isFinite(e)) outMax.y = Math.max(outMax.y, e);
        if (isFinite(f)) outMax.z = Math.max(outMax.z, f);
    }
}

function mergeVisibleBounds(
    width: number,
    visibleMinData: Float32Array,
    visibleMaxData: Float32Array,
    outMin: Vec3,
    outMax: Vec3
) {
    outMin.set(Infinity, Infinity, Infinity);
    outMax.set(-Infinity, -Infinity, -Infinity);

    for (let i = 0; i < width; i++) {
        const a = visibleMinData[i * 4];
        const b = visibleMinData[i * 4 + 1];
        const c = visibleMinData[i * 4 + 2];
        if (isFinite(a)) outMin.x = Math.min(outMin.x, a);
        if (isFinite(b)) outMin.y = Math.min(outMin.y, b);
        if (isFinite(c)) outMin.z = Math.min(outMin.z, c);

        const d = visibleMaxData[i * 4];
        const e = visibleMaxData[i * 4 + 1];
        const f = visibleMaxData[i * 4 + 2];
        if (isFinite(d)) outMax.x = Math.max(outMax.x, d);
        if (isFinite(e)) outMax.y = Math.max(outMax.y, e);
        if (isFinite(f)) outMax.z = Math.max(outMax.z, f);
    }
}

function visibleBoundsValid(outMin: Vec3, outMax: Vec3): boolean {
    if (!Number.isFinite(outMin.x) || !Number.isFinite(outMax.x)) return false;
    return outMin.x <= outMax.x && outMin.y <= outMax.y && outMin.z <= outMax.z;
}

class CalcBound {
    private device: GraphicsDevice;
    private splatParams = new Int32Array(3);
    private shader: Shader = null;
    private selectedMinTexture: Texture = null;
    private selectedMaxTexture: Texture = null;
    private visibleMinTexture: Texture = null;
    private visibleMaxTexture: Texture = null;
    private renderTarget: RenderTarget = null;
    private selectedMinRenderTarget: RenderTarget = null;
    private selectedMaxRenderTarget: RenderTarget = null;
    private visibleMinRenderTarget: RenderTarget = null;
    private visibleMaxRenderTarget: RenderTarget = null;
    private selectedMinData: Float32Array = null;
    private selectedMaxData: Float32Array = null;
    private visibleMinData: Float32Array = null;
    private visibleMaxData: Float32Array = null;
    /** 1×1 RGBA16F opaque — bound when resource has no splatColor (SOG); never sampled when minAlpha=0. */
    private dummySplatColor: Texture | null = null;

    constructor(device: GraphicsDevice) {
        this.device = device;
    }

    private ensureDummySplatColor(device: GraphicsDevice): Texture {
        if (this.dummySplatColor) return this.dummySplatColor;
        const t = new Texture(device, {
            name: 'calcBoundDummySplatColor',
            width: 1,
            height: 1,
            format: PIXELFORMAT_RGBA16F,
            mipmaps: false,
            addressU: ADDRESS_CLAMP_TO_EDGE,
            addressV: ADDRESS_CLAMP_TO_EDGE
        });
        const locked = t.lock();
        if (locked instanceof Uint16Array && locked.length >= 4) {
            const h1 = 0x3c00; // 1.0 in IEEE half
            locked[0] = h1;
            locked[1] = h1;
            locked[2] = h1;
            locked[3] = h1;
        }
        t.unlock();
        this.dummySplatColor = t;
        return t;
    }

    private getResources(width: number) {
        const { device } = this;

        if (!this.shader) {
            this.shader = ShaderUtils.createShader(device, {
                uniqueName: 'calcBoundShaderOpacityFraming',
                attributes: {
                    vertex_position: SEMANTIC_POSITION
                },
                vertexGLSL: vertexShader,
                fragmentGLSL: fragmentShader
            });
        }

        if (!this.selectedMinTexture || this.selectedMinTexture.width !== width) {
            if (this.selectedMinTexture) {
                this.selectedMinTexture.destroy();
                this.selectedMaxTexture.destroy();
                this.visibleMinTexture.destroy();
                this.visibleMaxTexture.destroy();
                this.renderTarget.destroy();
                this.selectedMinRenderTarget.destroy();
                this.selectedMaxRenderTarget.destroy();
                this.visibleMinRenderTarget.destroy();
                this.visibleMaxRenderTarget.destroy();
            }

            const createTexture = (name: string) => {
                return new Texture(device, {
                    name,
                    width,
                    height: 1,
                    format: PIXELFORMAT_RGBA32F,
                    mipmaps: false,
                    addressU: ADDRESS_CLAMP_TO_EDGE,
                    addressV: ADDRESS_CLAMP_TO_EDGE
                });
            };

            this.selectedMinTexture = createTexture('calcBoundSelectedMin');
            this.selectedMaxTexture = createTexture('calcBoundSelectedMax');
            this.visibleMinTexture = createTexture('calcBoundVisibleMin');
            this.visibleMaxTexture = createTexture('calcBoundVisibleMax');

            this.renderTarget = new RenderTarget({
                colorBuffers: [this.selectedMinTexture, this.selectedMaxTexture, this.visibleMinTexture, this.visibleMaxTexture],
                depth: false
            });

            this.selectedMinRenderTarget = new RenderTarget({
                colorBuffer: this.selectedMinTexture,
                depth: false
            });

            this.selectedMaxRenderTarget = new RenderTarget({
                colorBuffer: this.selectedMaxTexture,
                depth: false
            });

            this.visibleMinRenderTarget = new RenderTarget({
                colorBuffer: this.visibleMinTexture,
                depth: false
            });

            this.visibleMaxRenderTarget = new RenderTarget({
                colorBuffer: this.visibleMaxTexture,
                depth: false
            });

            this.selectedMinData = new Float32Array(width * 4);
            this.selectedMaxData = new Float32Array(width * 4);
            this.visibleMinData = new Float32Array(width * 4);
            this.visibleMaxData = new Float32Array(width * 4);
        }

        return {
            shader: this.shader,
            selectedMinTexture: this.selectedMinTexture,
            selectedMaxTexture: this.selectedMaxTexture,
            visibleMinTexture: this.visibleMinTexture,
            visibleMaxTexture: this.visibleMaxTexture,
            renderTarget: this.renderTarget,
            selectedMinRenderTarget: this.selectedMinRenderTarget,
            selectedMaxRenderTarget: this.selectedMaxRenderTarget,
            visibleMinRenderTarget: this.visibleMinRenderTarget,
            visibleMaxRenderTarget: this.visibleMaxRenderTarget,
            selectedMinData: this.selectedMinData,
            selectedMaxData: this.selectedMaxData,
            visibleMinData: this.visibleMinData,
            visibleMaxData: this.visibleMaxData
        };
    }

    async run(splat: Splat, selectionBound: BoundingBox, localBound: BoundingBox): Promise<void> {
        const device = splat.scene.graphicsDevice;
        const { scope } = device;

        const numSplats = splat.splatData.numSplats;
        const resource = splat.entity.gsplat.instance.resource as any;
        const transformA = resource.getTexture('transformA');
        const splatTransform = splat.transformTexture;
        const transformPalette = splat.transformPalette.texture;
        const splatState = splat.stateTexture;

        this.splatParams[0] = transformA.width;
        this.splatParams[1] = transformA.height;
        this.splatParams[2] = numSplats;

        const resources = this.getResources(transformA.width);

        const splatColorTex = resource.getTexture?.('splatColor') as Texture | undefined;
        const transformB = resource.getTexture?.('transformB') as Texture | undefined;
        const splatColorBind =
            splatColorTex ?? transformB ?? this.ensureDummySplatColor(device);

        const framingCfg = (splat.scene.config as any).boundFraming;
        const framingOn = framingCfg?.enabled !== false;
        const keepFrac =
            typeof framingCfg?.keepOpacityFraction === 'number' ? framingCfg.keepOpacityFraction : 0.8;

        let minAlpha = 0;
        if (framingOn && splatColorTex) {
            const opacity = splat.splatData.getProp('opacity') as Float32Array | undefined;
            minAlpha = minAlphaForOpacityKeepFraction(opacity, keepFrac);
            if (minAlpha >= 0.999) minAlpha = 0;
        }

        const drawAndRead = async (alphaCut: number) => {
            resolve(scope, {
                transformA,
                splatTransform,
                transformPalette,
                splatState,
                splatColor: splatColorBind,
                bound_framing_min_alpha: alphaCut,
                splat_params: this.splatParams
            });

            device.setBlendState(BlendState.NOBLEND);
            drawQuadWithShader(device, resources.renderTarget, resources.shader);

            await Promise.all([
                resources.selectedMinTexture.read(0, 0, transformA.width, 1, {
                    renderTarget: resources.selectedMinRenderTarget,
                    data: resources.selectedMinData,
                    immediate: false
                }),
                resources.selectedMaxTexture.read(0, 0, transformA.width, 1, {
                    renderTarget: resources.selectedMaxRenderTarget,
                    data: resources.selectedMaxData,
                    immediate: false
                }),
                resources.visibleMinTexture.read(0, 0, transformA.width, 1, {
                    renderTarget: resources.visibleMinRenderTarget,
                    data: resources.visibleMinData,
                    immediate: false
                }),
                resources.visibleMaxTexture.read(0, 0, transformA.width, 1, {
                    renderTarget: resources.visibleMaxRenderTarget,
                    data: resources.visibleMaxData,
                    immediate: false
                })
            ]);
        };

        await drawAndRead(minAlpha);

        mergeSelectedBounds(transformA.width, resources.selectedMinData, resources.selectedMaxData, v1, v2);
        selectionBound.setMinMax(v1, v2);

        mergeVisibleBounds(transformA.width, resources.visibleMinData, resources.visibleMaxData, v3, v4);

        if (minAlpha > 0 && !visibleBoundsValid(v3, v4)) {
            await drawAndRead(0);
            mergeSelectedBounds(transformA.width, resources.selectedMinData, resources.selectedMaxData, v1, v2);
            selectionBound.setMinMax(v1, v2);
            mergeVisibleBounds(transformA.width, resources.visibleMinData, resources.visibleMaxData, v3, v4);
        }

        localBound.setMinMax(v3, v4);
    }
}

export { CalcBound };
